import {
  useGetAnalysticsSearchMutation,
  useGetPbiEmbedTokenQuery,
  useTypedSelector,
  useFetchAiWorkersByCategoryMutation,
  categoryType,
  useRefreshAnalyticsReportMutation,
  useFetchRefreshAccessTokenMutation,
  useEntityResourcePermissions,
  useAskAgenticAnalyticsMutation,
  parseSkillsAttrValue,
  type AskAgenticAnalyticsResponse,
} from '@circuitry-ai/doc-data';
import CtyPromptInput, { checkType, CtyButton, CtyShowToast, promptInputType, ConvertToLocalDate, useHasApplicationResource } from '@circuitry-ai/doc-ui';
import { Icon } from '@iconify/react';
import { Chip, Select, SelectItem } from '@nextui-org/react';
import { models, Embed } from 'powerbi-client';
import { PowerBIEmbed } from 'powerbi-client-react';
import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import AgenticAnalyticsDetails from './AgenticAnalyticsDetails';


function isAgentHistoryDeepLink(url: URL): boolean {
  return /\/history\/?$/i.test(url.pathname) || url.pathname.endsWith('/history');
}

function openAnalystDashboardHistoryLink(rawUrl?: string) {
  if (!rawUrl) return;
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    if (!isAgentHistoryDeepLink(parsed)) {
      window.open(rawUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    localStorage.removeItem('table-config-agent-history-table');
    window.open(`${parsed.pathname}${parsed.search}${parsed.hash}`, '_blank', 'noopener,noreferrer');
  } catch {
    window.open(rawUrl, '_blank', 'noopener,noreferrer');
  }
}

// Renew the Power BI embed token this long before it expires.
const EMBED_TOKEN_RENEW_BUFFER_MS = 5 * 60 * 1000;
// Used when the API does not return a parseable expiration.
const EMBED_TOKEN_FALLBACK_RENEW_MS = 10 * 60 * 1000;
// Minimum gap between renewals triggered by Power BI "TokenExpired" errors.
const EMBED_TOKEN_ERROR_RENEW_THROTTLE_MS = 30 * 1000;

// Accepts an ISO timestamp or an epoch (seconds or ms). Power BI expirations are UTC, so a
// timestamp without a timezone is treated as UTC rather than local time.
function parseExpirationTimestamp(expiration?: string | number): number | null {
  if (expiration === undefined || expiration === null || expiration === '') return null;
  if (typeof expiration === 'number' || /^\d+$/.test(String(expiration).trim())) {
    const epoch = Number(expiration);
    return epoch < 1e12 ? epoch * 1000 : epoch;
  }
  const value = String(expiration).trim();
  const hasTimezone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const time = new Date(hasTimezone ? value : `${value}Z`).getTime();
  return Number.isNaN(time) ? null : time;
}

// Resolves when the embed token expires, from whichever expiry field the API returned.
function getEmbedTokenExpiresAt(
  token: { embed_token_expiration?: string | number; expiration?: string | number; expires_in?: number },
  fetchedAt: number
): number | null {
  const absolute = parseExpirationTimestamp(token.embed_token_expiration) ?? parseExpirationTimestamp(token.expiration);
  if (absolute !== null) return absolute;
  const expiresIn = Number(token.expires_in);
  return Number.isFinite(expiresIn) && expiresIn > 0 ? fetchedAt + expiresIn * 1000 : null;
}

function attachPowerBiHistoryLinkHandler(report: Embed | null) {
  const embed = report as (Embed & { __historyLinkHandlerAttached?: boolean; on?: Function }) | null;
  if (!embed?.on || embed.__historyLinkHandlerAttached) return;
  embed.__historyLinkHandlerAttached = true;
  embed.on('dataHyperlinkClicked', (event: { detail?: { url?: string } }) => {
    openAnalystDashboardHistoryLink(event?.detail?.url);
  });
}

/* eslint-disable-next-line */
export interface AnalyticsDetailsProps {
  readonly setRefreshStatus: (status: 'idle' | 'success' | 'error' | 'loading') => void;
  readonly setLastRefreshedReportData: (data: any) => void;
  readonly lastRefreshedReportData: any;

}

interface AnalystData {
  name: string;
  analyst_type?: string;
  dataset_id: string;
  dataset_workspace_id: string;
  skills?: string[];
}

interface AgenticHistoryItem {
  id: string;
  question: string;
  use_cache: boolean;
  response?: AskAgenticAnalyticsResponse;
  error?: string;
  loading?: boolean;
}

const SPLIT_MIN_PCT = 25;
const SPLIT_MAX_PCT = 75;

type DashboardViewMode = 'agentic' | 'powerbi' | 'both';

function createHistoryId(): string {
  if (typeof crypto !== 'undefined') {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    if (typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    }
  }
  return `${Date.now().toString(16)}-${new Date().getTime().toString(16)}`;
}

function normalizeValue(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

/** All normalized analyst names that use the agentic ask API (not Power BI). */
const AGENTIC_ANALYST_NAMES = new Set([
  'conversation ainalyst', 'conversation analyst', 'conversation_ainalyst', 'conversation_analyst',
  'knowledge ainalyst', 'knowledge analyst', 'knowledge_ainalyst', 'knowledge_analyst',
  'agent history analyst', 'agent_history_analyst', 'agent history ainalyst', 'agent_history_ainalyst', 'agent history', 'agent_history',
  'claim_score_agent', 'claim score agent', 'claim score analyst', 'claim_score_analyst', 'claim score ainalyst', 'claim_score_ainalyst', 'claim score', 'claim_score', 'claim score agent analyst', 'claim score agent ainalyst', 'claim_score_agent_ainalyst', 'claim_score_agent_analyst',
  'vhr agent analyst', 'vhr_agent_analyst', 'vhr agent ainalyst', 'vhr_agent_ainalyst', 'vhr score analyst', 'vhr_score_analyst', 'vhr score ainalyst', 'vhr_score_ainalyst', 'vhr agent', 'vhr_agent', 'vhr score', 'vhr_score',
  'claim adjudicator analyst', 'claim_adjudicator_analyst', 'claim adjudicator ainalyst', 'claim_adjudicator_ainalyst',
  'invoice and payments analyst', 'invoice_and_payments_analyst', 'invoice and payments ainalyst', 'invoice_and_payments_ainalyst',
  'ro matcher', 'ro_matcher', 'ro matcher agent analyst', 'ro_matcher_agent_analyst', 'ro matcher agent ainalyst', 'ro_matcher_agent_ainalyst',
  'ro matcher analyst', 'ro_matcher_analyst', 'ro matcher ainalyst', 'ro_matcher_ainalyst',
  'invoice matcher analyst', 'invoice_matcher_analyst', 'invoice matcher ainalyst', 'invoice_matcher_ainalyst', 'invoice matcher', 'invoice_matcher',
  'reconciliation analyst', 'reconciliation_analyst', 'reconciliation ainalyst', 'reconciliation_ainalyst',
]);

/** Maps the current analyst display name to the agentic API `skill` payload value. */
function resolveAgenticSkill(analystName?: string): string {
  const n = normalizeValue(analystName);
  if (n === 'knowledge ainalyst' || n === 'knowledge_ainalyst' || n === 'knowledge analyst' || n === 'knowledge_analyst') {
    return 'knowledge-analyst';
  }
  if (n === 'agent history analyst' || n === 'agent_history_analyst' || n === 'agent history ainalyst' || n === 'agent_history_ainalyst' || n === 'agent history' || n === 'agent_history') {
    return 'agent-history-analyst';
  }
  if (n === 'claim_score_agent' || n === 'claim score agent' || n === 'claim score analyst' || n === 'claim_score_analyst' || n === 'claim score ainalyst' || n === 'claim_score_ainalyst' || n === 'claim score' || n === 'claim_score' || n === 'claim score agent analyst' || n === 'claim score agent ainalyst' || n === 'claim score_agent_ainalyst' || n === 'claim score_agent_analyst') {
    return 'claim-score-analyst';
  }
  if (n === 'vhr agent analyst' || n === 'vhr_agent_analyst' || n === 'vhr agent ainalyst' || n === 'vhr_agent_ainalyst' || n === 'vhr score analyst' || n === 'vhr_score_analyst' || n === 'vhr score ainalyst' || n === 'vhr_score_ainalyst' || n === 'vhr agent' || n === 'vhr_agent' || n === 'vhr score' || n === 'vhr_score') {
    return 'vhr-agent-analyst';
  }
  if (n === 'claim adjudicator analyst' || n === 'claim_adjudicator_analyst' || n === 'claim adjudicator ainalyst' || n === 'claim_adjudicator_ainalyst') {
    return 'claim-adjudicator-analyst';
  }
  if (n === 'invoice and payments analyst' || n === 'invoice_and_payments_analyst' || n === 'invoice and payments ainalyst' || n === 'invoice_and_payments_ainalyst') {
    return 'invoice-and-payments-analyst';
  }
  if (
    n === 'ro matcher' || n === 'ro_matcher' ||
    n === 'ro matcher agent analyst' || n === 'ro_matcher_agent_analyst' || n === 'ro matcher agent ainalyst' || n === 'ro_matcher_agent_ainalyst' ||
    n === 'ro matcher analyst' || n === 'ro_matcher_analyst' || n === 'ro matcher ainalyst' || n === 'ro_matcher_ainalyst' ||
    n === 'invoice matcher analyst' || n === 'invoice_matcher_analyst' || n === 'invoice matcher ainalyst' || n === 'invoice_matcher_ainalyst' || n === 'invoice matcher' || n === 'invoice_matcher'
  ) {
    return 'ro-matcher-agent-analyst';
  }
  if (n === 'reconciliation analyst' || n === 'reconciliation_analyst' || n === 'reconciliation ainalyst' || n === 'reconciliation_ainalyst') {
    return 'reconciliation-analyst';
  }
  return 'conversations-analyst';
}

function extractApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { data?: unknown })?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const body = data as { message?: unknown };
    if (typeof body.message === 'string' && body.message.trim()) {
      return body.message;
    }
  }
  const message = (error as { message?: unknown })?.message;
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
}

function extractApiErrorSessionId(error: unknown): string | undefined {
  const sessionId = (error as { session_id?: unknown })?.session_id;
  if (typeof sessionId === 'string' && sessionId.trim()) return sessionId;
  return undefined;
}

export function AnalyticsDetails(props: Readonly<AnalyticsDetailsProps>) {

  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('reportId');
  const aiworkerId = searchParams.get('aiworkerId');
  const analystTypeParam = searchParams.get('analystType');
  const { canUpdate } = useEntityResourcePermissions('analyst', aiworkerId ?? undefined);
  const hasNlCapabilities = useHasApplicationResource('nl_capabilities');
  const { setLastRefreshedReportData, setRefreshStatus, lastRefreshedReportData } = props;
  const location = useLocation();
  const { report } = location?.state ?? {};
  const analystName = report?.name;

  const token = useTypedSelector((state) => state.auth.token);
  const [skip, setSkip] = useState<boolean>(true);
  const [embedKey] = useState<number>(0);
  const { t } = useTranslation();
  const [analystData, setAnalystData] = useState<AnalystData | null>(null);
  
  const { data: embedToken, fulfilledTimeStamp: embedTokenFetchedAt, refetch: refetchEmbedToken } = useGetPbiEmbedTokenQuery(
    { reportId: reportId ?? '', datasetId: analystData?.dataset_id ?? '' },
    { skip: skip || !reportId || !analystData?.dataset_id, refetchOnMountOrArgChange: true }
  );
  const lastEmbedTokenRenewRef = useRef<number>(0);

  const renewEmbedToken = () => {
    lastEmbedTokenRenewRef.current = Date.now();
    void refetchEmbedToken();
  };

  // Keep the embed token fresh. PowerBIEmbed applies a changed accessToken to the live report
  // via setAccessToken, so filters and page state are preserved without re-embedding.
  useEffect(() => {
    if (!embedToken?.embed_token) return undefined;
    const expiresAt = getEmbedTokenExpiresAt(embedToken, embedTokenFetchedAt ?? Date.now());
    const renewAt = expiresAt === null
      ? Date.now() + EMBED_TOKEN_FALLBACK_RENEW_MS
      : expiresAt - EMBED_TOKEN_RENEW_BUFFER_MS;

    const timer = setTimeout(renewEmbedToken, Math.max(renewAt - Date.now(), 0));
    // Background tabs throttle timers, so also check when the user returns to the tab.
    const onVisibilityChange = () => {
      if (!document.hidden && Date.now() >= renewAt) renewEmbedToken();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [embedToken, embedTokenFetchedAt]);

  const handlePowerBiError = (event?: { detail?: { message?: string } }) => {
    if (event?.detail?.message !== 'TokenExpired') return;
    if (Date.now() - lastEmbedTokenRenewRef.current < EMBED_TOKEN_ERROR_RENEW_THROTTLE_MS) return;
    renewEmbedToken();
  };

  const [fetchAiWorkersByCategory] = useFetchAiWorkersByCategoryMutation();
  const [refreshAnalyticsReport, { isLoading: isAnalyticsRefreshLoading }] = useRefreshAnalyticsReportMutation();

  const [getAnalysticsSearch, { isLoading: isAnalyticsLoading }] = useGetAnalysticsSearchMutation();
  const [askAgenticAnalytics, { isLoading: isAgenticAnalyticsLoading }] = useAskAgenticAnalyticsMutation();
  const [fetchRefreshAccessToken] = useFetchRefreshAccessTokenMutation();
  const embedRef = useRef<Embed | null>(null);
  const focusRef = useRef<HTMLTextAreaElement>(null);
  const [chipList, setChipList] = useState<string[]>([]);
  const [isSpeechMode, setIsSpeechMode] = useState<boolean>(false);
  const skipRef = useRef<boolean>(false);
  const [refreshData, setRefreshData] = useState<any>(null);
  const aiWordersAttrs = new Map<string, string>();
  const [agenticHistory, setAgenticHistory] = useState<AgenticHistoryItem[]>([]);
  const [useCache, setUseCache] = useState(true);
  const [isAgenticPanelOpen, setIsAgenticPanelOpen] = useState(false);
  const [dashboardViewMode, setDashboardViewMode] = useState<DashboardViewMode>('both');

  // Resizable split (only meaningful on wide screens where the two panels sit side by side).
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const splitDragRef = useRef<{ startX: number; startPct: number } | null>(null);
  const [agenticWidthPct, setAgenticWidthPct] = useState(50);
  const [isWideViewport, setIsWideViewport] = useState(false);

  useEffect(() => {
    if (typeof globalThis === 'undefined' || !globalThis.matchMedia) return undefined;
    const mql = globalThis.matchMedia('(min-width: 1280px)');
    const sync = () => setIsWideViewport(mql.matches);
    sync();
    mql.addEventListener('change', sync);
    return () => mql.removeEventListener('change', sync);
  }, []);

  const onSplitPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    splitDragRef.current = { startX: event.clientX, startPct: agenticWidthPct };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onSplitPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = splitDragRef.current;
    const container = splitContainerRef.current;
    if (!drag || !container) return;
    const total = container.getBoundingClientRect().width;
    if (total <= 0) return;
    const deltaPct = ((event.clientX - drag.startX) / total) * 100;
    const next = Math.min(SPLIT_MAX_PCT, Math.max(SPLIT_MIN_PCT, drag.startPct + deltaPct));
    setAgenticWidthPct(next);
  };

  const onSplitPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!splitDragRef.current) return;
    splitDragRef.current = null;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      /* no-op */
    }
  };

  useEffect(() => {
    if (token && skip) {
      focusRef.current?.focus();
      setSkip(false);
    }
  }, [token, skip]);

  useEffect(() => {
    if (token && !skipRef.current) {
      skipRef.current = true;
    }
  }, [token]);

  const setAiWordersAttrs = (report: any) => {
    if (!report?.aiworkers_attrs || !Array.isArray(report.aiworkers_attrs)) return;
    const skillsAttr = report.aiworkers_attrs.find((item: any) => item.attr_name === 'skills');
    report.aiworkers_attrs.map((item: any) => {
      aiWordersAttrs.set(item.attr_name, item.attr_value);
    });
    setAnalystData({
      name: report.name ?? '',
      analyst_type: aiWordersAttrs.get('analyst_type') ?? '',
      dataset_id: aiWordersAttrs.get('dataset_id') ?? '',
      dataset_workspace_id: aiWordersAttrs.get('dataset_workspace_id') ?? '',
      skills: parseSkillsAttrValue(skillsAttr?.attr_value),
    });
  };

  const isDecisionAnalyst =
    analystTypeParam === 'decision' || analystData?.analyst_type === 'decision';
  const isAgenticAnalyst = () =>
    isDecisionAnalyst || AGENTIC_ANALYST_NAMES.has(normalizeValue(analystName ?? analystData?.name));

  useEffect(() => {
    if (!skip) {
      if (!report) {
        void updateAnalystData();
      } else {
        setAiWordersAttrs(report);
      }

      void (async () => {
        try {
          const response = await fetchRefreshAccessToken({ aiworker_id: aiworkerId ?? '' }).unwrap();
          setRefreshData(response);
        } catch (error) {
          CtyShowToast(checkType.error, (error as Error)?.message);
        }
      })();
    }
  }, [report, skip]);

  const updateAnalystData = async () => {
    try {
      const response = await fetchAiWorkersByCategory(categoryType.ANALYST);
      const report = (response.data ?? []).find((item: any) => {
        if (aiworkerId && item.aiworker_id === aiworkerId) return true;
        return item.aiworkers_attrs?.some((attr: any) =>
          attr.attr_name === 'report_id' && attr.attr_value === reportId
        );
      });
      setAiWordersAttrs(report);
    } catch (e) {
      console.error('Error fetching analyst data:', (e as Error)?.message);
    }
  };

  useEffect(() => {
    // Only trigger initial refresh if there's no existing last refreshed data
    if (
      analystData?.dataset_id &&
      analystData?.dataset_workspace_id &&
      !lastRefreshedReportData &&
      refreshData?.access_token
    ) {
      const initialRefresh = async () => {
        const powerBiUrl = `https://api.powerbi.com/v1.0/myorg/groups/${analystData?.dataset_workspace_id}/datasets/${analystData?.dataset_id}/refreshes`;

        try {
          // Get the latest refresh status
          const response = await fetch(powerBiUrl, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${refreshData?.access_token}`,
            },
          });

          if (!response.ok) {
            throw new Error('Failed to fetch refresh status');
          }

          const data = await response.json();
          const latestRefresh = data.value[0]; // Get the most recent refresh
          // If the latest refresh is success then set the status to success
          if (latestRefresh.status === 'Completed') {
            setLastRefreshedReportData(latestRefresh);
            setRefreshStatus('success');

          }
        } catch (error) {
          console.error('Error during initial refresh check:', error);
          setRefreshStatus('error');
        }
      };

      void initialRefresh();
    }
  }, [refreshData?.access_token, analystData]);

  const handleRefresh = async () => {
    if (!aiworkerId) {
      CtyShowToast(checkType.error, t('Missing analyst id. Please reopen this analyst and try again.'));
      return;
    }

    try {
      const response = await refreshAnalyticsReport({ aiworker_id: aiworkerId }).unwrap();
      CtyShowToast(checkType.success, response?.message ?? t('Refresh started successfully'));
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message);
    }
  };

  const handleAgenticSearch = async (normalizedQuestion: string) => {
    const historyId = createHistoryId();
    const useCacheForRequest = useCache;
    setAgenticHistory((previousItems) => [
      ...previousItems,
      { id: historyId, question: normalizedQuestion, use_cache: useCacheForRequest, loading: true },
    ]);
    setIsAgenticPanelOpen(true);

    try {
      const skillPayload = isDecisionAnalyst
        ? (analystData?.skills?.length ? analystData.skills : [])
        : resolveAgenticSkill(analystName ?? analystData?.name);
      const response = await askAgenticAnalytics({
        question: normalizedQuestion,
        skills: skillPayload,
        verbose: false,
        use_cache: useCacheForRequest,
        isDecision: isDecisionAnalyst,
      }).unwrap();
      setAgenticHistory((previousItems) =>
        previousItems.map((item) =>
          item.id === historyId ? { ...item, response, loading: false } : item
        )
      );
    } catch (error) {
      const message = extractApiErrorMessage(error, t('Failed to fetch analyst response'));
      const sessionId = extractApiErrorSessionId(error);
      setAgenticHistory((previousItems) =>
        previousItems.map((item) =>
          item.id === historyId
            ? { ...item, error: message, response: sessionId ? ({ session_id: sessionId } as AskAgenticAnalyticsResponse) : item.response, loading: false }
            : item
        )
      );
      CtyShowToast(checkType.error, message);
    }
  };

  const handlePowerBiSearch = async (inputValue: string) => {
    try {
      const response = await getAnalysticsSearch({
        user_query: inputValue,
        tablename: analystData?.name
      }).unwrap();

      if (response.length == 0) {
        CtyShowToast(checkType.info, `Couldn't process your request.Could you please try rephrasing it or provide more details`);
        return;
      }
      const value: any[] = [];
      const chipsList: string[] = [];
      response.forEach((element: any) => {
        value.push({
          $schema: element.$schema,
          target: element.target,
          logicalOperator: element.logicalOperator,
          conditions: element.conditions,
          filterType: models.FilterType.Advanced
        });
      });
      if (embedRef.current) {
        const reportEmbed = embedRef.current as any;
        await reportEmbed.setFilters(value);

        const reportFilters = await reportEmbed.getFilters();
        reportFilters.forEach((filter: any) => {
          if (filter?.filterType !== models.FilterType.Advanced) return;
          filter.conditions?.forEach((condition: any) => {
            const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
            chipsList.push(
              dateRegex.test(condition.value)
                ? ConvertToLocalDate(condition.value)
                : `${condition.value}`
            );
          });
        });
        setChipList(chipsList);
      }

      CtyShowToast(checkType.success, 'Search completed successfully');
    } catch (error) {
      CtyShowToast(checkType.error, (error as Error)?.message);
    }
  };

  const handleSearch = async (inputValue: string) => {
    if (!inputValue) return;

    if (isAgenticAnalyst()) {
      const normalizedQuestion = inputValue.trim();
      if (!normalizedQuestion) return;
      await handleAgenticSearch(normalizedQuestion);
      return;
    }

    await handlePowerBiSearch(inputValue);
  };

  const clearFilters = async () => {
    if (embedRef?.current) {
      const reportEmbed = embedRef.current as any;
      await reportEmbed.removeFilters();
      setChipList([]);
    }
  };

  const effectiveViewMode: DashboardViewMode = isDecisionAnalyst ? 'agentic' : dashboardViewMode;
  const shouldRenderAgenticPanel =
    isAgenticAnalyst() &&
    (
      effectiveViewMode === 'agentic' ||
      (effectiveViewMode === 'both' && isAgenticPanelOpen && agenticHistory.length > 0)
    );
  const shouldRenderPowerBi =
    !isDecisionAnalyst && (!isAgenticAnalyst() || effectiveViewMode !== 'agentic');
  const isSplitView =
    shouldRenderAgenticPanel &&
    shouldRenderPowerBi &&
    effectiveViewMode === 'both';

  return (
    <div id="analytics-powerBI-board" className="flex h-full min-h-0 flex-col">
      <div className={`flex flex-col lg:flex-row ${hasNlCapabilities ? 'justify-between' : 'justify-end'} gap-3 mt-1 mb-2 bg-red-d200`}>
        {hasNlCapabilities ? (
          <div className="flex w-[100%] rounded-sm gap-3 items-end transition-colors analytics-powerBI-board">
            <CtyPromptInput
              id='powerbi-dashboard-search'
              elementType={promptInputType.INPUT}
              enableSearch={true}
              enableSendBtn={true}
              enableSpeak={true}
              handleSendClick={handleSearch}
              placeholder={t('Ask Anything')}
              disabled={isAnalyticsLoading || isAgenticAnalyticsLoading}
              inputHeight={'ctyPromptInput'}
              setIsSpeechMode={setIsSpeechMode}
              isSpeechMode={isSpeechMode}
            />
          </div>
        ) : null}
        {!isDecisionAnalyst ? (
          <div className='flex gap-2 items-center  lg:justify-end bg-osrange-300'>
            {isAgenticAnalyst() && agenticHistory.length > 0 ? (
              <Select
                size="sm"
                selectedKeys={[dashboardViewMode]}
                onSelectionChange={(keys) => {
                  const selectedKey = Array.from(keys)[0];
                  if (!selectedKey) return;
                  setDashboardViewMode(String(selectedKey) as DashboardViewMode);
                }}
                aria-label={t('Dashboard view mode')}
                className="w-[190px]"
                variant="bordered"
              >
                <SelectItem key="agentic" value="agentic">
                  {t('Agentic Analytics')}
                </SelectItem>
                <SelectItem key="powerbi" value="powerbi">
                  {t('Power BI Dashboard')}
                </SelectItem>
                <SelectItem key="both" value="both">
                  {t('Both')}
                </SelectItem>
              </Select>
            ) : null}
            <CtyButton id="refresh-analystics-btn" variant="bordered" className="font-bold" size="sm" onPress={handleRefresh} startContent={<Icon icon="system-uicons:refresh" width="21" height="21" />} isLoading={isAnalyticsRefreshLoading} isDisabled={!canUpdate}>Refresh</CtyButton>
          </div>
        ) : null}
      </div>
      {!isAgenticAnalyst() ? (
        <div className='mb-4 flex gap-1'>
          {chipList.length > 0 && <Chip id="clear-filters-btn" color="danger" variant="faded" onClose={clearFilters} size="sm">Clear</Chip>}

          {chipList.map(item => (
            <Chip key={item} size="sm" color="success" variant='dot'>{item}</Chip>
          ))}
        </div>
      ) : null}
      <div
        ref={splitContainerRef}
        className={`flex h-full min-h-0 w-full ${isSplitView ? 'flex-col gap-3 xl:flex-row xl:gap-0' : 'flex-col gap-3'}`}
      >
        {shouldRenderAgenticPanel ? (
          <div
            className="h-full min-h-0 w-full"
            style={isSplitView && isWideViewport ? { width: `${agenticWidthPct}%` } : undefined}
          >
            <AgenticAnalyticsDetails
              history={agenticHistory}
              name={analystName ?? analystData?.name}
              useCache={useCache}
              onUseCacheChange={setUseCache}
              isSplitView={isSplitView}
              showCloseButton={!isDecisionAnalyst}
              onAsk={(question: string) => {
                void handleSearch(question);
              }}
              onDrilldown={(question: string) => {
                void handleSearch(question);
              }}
              onClose={() => {
                setDashboardViewMode('powerbi');
              }}
            />
          </div>
        ) : null}
        {isSplitView ? (
          <hr
            aria-orientation="vertical"
            aria-label={t('Resize panels')}
            className="hidden xl:block mx-1 h-auto w-1 shrink-0 cursor-col-resize touch-none select-none self-stretch rounded border-0 bg-default-200 hover:bg-primary/40"
            onPointerDown={onSplitPointerDown}
            onPointerMove={onSplitPointerMove}
            onPointerUp={onSplitPointerUp}
            onPointerCancel={onSplitPointerUp}
          />
        ) : null}
        {shouldRenderPowerBi ? (
          <div
            className={isSplitView ? 'w-full xl:flex-1 xl:min-w-0' : 'w-full'}
          >
          <PowerBIEmbed
            key={embedKey}
            embedConfig={{
              type: 'report',
              id: reportId ?? undefined,
              embedUrl: `https://app.powerbi.com/reportEmbed?reportId=${reportId}&filterPaneEnabled=false`,
              accessToken: embedToken?.embed_token,
              tokenType: models.TokenType.Embed,
              settings: {
                hyperlinkClickBehavior: models.HyperlinkClickBehavior.RaiseEvent,
              },
            }}
            eventHandlers={
              new Map([
                ['loaded', () => attachPowerBiHistoryLinkHandler(embedRef.current)],
                ['error', (event?: any) => handlePowerBiError(event)],
              ])
            }
            cssClassName="report-container"
            getEmbeddedComponent={(embeddedComponent) => {
              embedRef.current = embeddedComponent as Embed;
              attachPowerBiHistoryLinkHandler(embedRef.current);
            }}
          />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default AnalyticsDetails;